/**
 * `GET /meta` — what a client that cannot update itself learns from the server.
 *
 * The endpoint exists because the MVP is an installable PWA and a later client is native: both can
 * be running an old build that the server has to keep working with. `features` is how a capability
 * is switched off server-side without shipping a client release — for instance if a provider's terms
 * change — and `minimum_client` is how a client learns it must prompt for an upgrade.
 *
 * Every limit here is enforced and carries a number. A cap with no measured basis behind it would be
 * a fabricated threshold that every client then enforces (ADR-007), so a limit the server cannot
 * defend is simply not reported.
 */

import { z } from "zod";

import { IsoDateTime } from "./common.ts";

/** A capability the server can turn off without a client release. */
export const FeatureFlags = z.object({
  capture_url: z.boolean().describe("Accepting a URL as a place's source (ADR-004)."),
  capture_screenshot: z.boolean().describe("Screenshot capture. Specified, not implemented in v1."),
  push: z.boolean().describe("Push notifications. Planned, not implemented."),
  /**
   * Public mode: an unauthenticated request is served as one shared account, so every visitor sees
   * the same data without signing in. A client reads this rather than inferring it from a 401 —
   * the two behaviours need different screens, and guessing the mode from a failed request would
   * make an ordinary signed-out visitor indistinguishable from a deployment with no sign-in.
   *
   * On by default in this phase. `PUBLIC_MODE=false` turns it off on the server, restoring the
   * sign-in requirement for every route.
   */
  public_mode: z.boolean().describe("Requests without valid credentials are served as one shared account; all data is shared."),
}).meta({ id: "FeatureFlags", description: "Server-side capability switches, read by every client on start." });

/**
 * The oldest client build the server still supports, per platform.
 *
 * A platform absent from this map, or present with `null`, means the server has no minimum for it —
 * which for iOS is the honest state, since no iOS build exists.
 */
export const MinimumClient = z.object({
  web: z.string().nullable(),
  android: z.string().nullable(),
  ios: z.string().nullable(),
}).meta({ id: "MinimumClient", description: "Oldest supported build per platform. Null means no minimum is enforced." });

/** Operational limits the server enforces, never a guessed default. */
export const MetaLimits = z.object({
  plans_per_day: z.int().describe("Enforced: each plan can cost routing requests against a free tier."),
}).meta({ id: "MetaLimits", description: "Operational limits the server enforces." });

/**
 * The response.
 *
 * `server_time` is an instant with offset, not a date: a client uses it to detect clock skew, and a
 * date would not be able to detect a skew smaller than a day.
 */
export const MetaResponse = z.object({
  api_version: z.literal("v1").describe("The path version this server speaks. The only compatibility axis (ADR-005)."),
  server_time: IsoDateTime,
  minimum_client: MinimumClient,
  features: FeatureFlags,
  limits: MetaLimits,
}).meta({ id: "MetaResponse", description: "GET /meta. Unauthenticated, so a broken session can still learn what changed." });

export type MetaResponse = z.infer<typeof MetaResponse>;
export type FeatureFlags = z.infer<typeof FeatureFlags>;
